import { CheckCircle } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import type { AdminBusiness } from '@/api/businesses-admin';
import { formatWeekdayDateTime, formatDateTime } from '@/lib/format';
import { statusTone } from '@/pages/account/components/status-tone';

const cap = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ') : '—';

export interface ColumnActions {
  onLeaderApprove: (row: AdminBusiness) => void;
  onBodApprove: (row: AdminBusiness) => void;
}

/** Column definitions for the HRM businesses admin DataTable. */
export function getBusinessAdminColumns(
  actions: ColumnActions,
): ColumnDef<AdminBusiness, unknown>[] {
  return [
    {
      id: 'business_id',
      header: 'Code',
      cell: ({ row }) => (
        <span
          style={{
            color: 'var(--vn-primary-text)',
            fontWeight: 'var(--vn-font-weight-medium)',
          }}
        >
          IBN-{row.original.business_id}
        </span>
      ),
    },
    {
      id: 'employee_name',
      header: 'Employee',
      cell: ({ row }) => (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <span
            style={{
              fontWeight: 'var(--vn-font-weight-medium)',
              fontSize: 'var(--vn-font-size-sm)',
            }}
          >
            {row.original.employee_name}
          </span>
          <span
            style={{
              color: 'var(--vn-fg-muted)',
              fontSize: 'var(--vn-font-size-xs)',
            }}
          >
            {row.original.department_name}
          </span>
        </div>
      ),
    },
    {
      id: 'business_type',
      header: 'Type',
      cell: ({ row }) => cap(row.original.business_type),
    },
    {
      id: 'business_dates',
      header: 'Business date',
      cell: ({ row }) => (
        <span
          style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}
        >
          {formatWeekdayDateTime(row.original.business_startdate)}
          {' – '}
          {formatWeekdayDateTime(row.original.business_enddate)}
        </span>
      ),
    },
    {
      id: 'business_place',
      header: 'Location',
      cell: ({ row }) => row.original.business_place || '—',
    },
    {
      id: 'business_leader_status',
      header: 'Leader',
      cell: ({ row }) => (
        <StatusPill tone={statusTone(row.original.business_leader_status)}>
          {cap(row.original.business_leader_status)}
        </StatusPill>
      ),
    },
    {
      id: 'business_bod_status',
      header: 'BOM/BOD',
      cell: ({ row }) => (
        <StatusPill tone={statusTone(row.original.business_bod_status)}>
          {cap(row.original.business_bod_status)}
        </StatusPill>
      ),
    },
    {
      id: 'business_status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill tone={statusTone(row.original.business_status)}>
          {cap(row.original.business_status)}
        </StatusPill>
      ),
    },
    {
      id: 'business_created_at',
      header: 'Created',
      cell: ({ row }) => (
        <span
          style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}
        >
          {formatDateTime(row.original.business_created_at)}
        </span>
      ),
    },
    {
      id: 'row_actions',
      header: '',
      cell: ({ row }) => {
        const b = row.original;
        const canLeader =
          b.business_leader_status === 'pending' || b.business_status === 'pending';
        const canBod =
          b.business_leader_status !== 'pending' &&
          b.business_bod_status === 'pending';
        if (!canLeader && !canBod) return null;
        return (
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            {canLeader && (
              <Button
                size="sm"
                variant="ghost"
                startIcon={<CheckCircle size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  actions.onLeaderApprove(b);
                }}
              >
                Leader
              </Button>
            )}
            {canBod && (
              <Button
                size="sm"
                variant="ghost"
                startIcon={<CheckCircle size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  actions.onBodApprove(b);
                }}
              >
                BOD
              </Button>
            )}
          </div>
        );
      },
    },
  ];
}
